import { useQuery } from '@tanstack/react-query';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';

export type CourseMappingsPagePropsResponse = {
  referenceData: {
    pageSizes: number[];
    countries: Record<string, { alpha3Code: string; countryLabel: string; nationalityLabel: string }>;
    educationLevels: Record<string, { name: string }>;
    courses: Record<string, { name: string; code: string }>;
  };
  initialQueryParameters: {
    pageSize: number;
    sort: string;
  };
};

export type ReferenceData = CourseMappingsPagePropsResponse['referenceData'];
export type InitialQueryParameter = CourseMappingsPagePropsResponse['initialQueryParameters'];

export type CourseMappingsPageInitialProps = {
  referenceData: ReferenceData;
  initialQueryParameters: InitialQueryParameter;
};

export function useCourseMappingsPageProps() {
  const request = useRequest();
  const { isLoading, data, isError, error } = useQuery({
    queryKey: ['useCourseMappingsPageProps'],
    queryFn: async () => {
      const response = await request<CourseMappingsPagePropsResponse>(
        `${config.apiHost}/private/v1/course_mappings/page_props`,
        { isExpectedResponse, method: 'GET' }
      );

      const sortValues = response.initialQueryParameters.sort.split(',');
      const sort = sortValues.length > 0 ? sortValues[0] : '-createdAt';

      return {
        referenceData: response.referenceData,
        initialQueryParameters: { sort, pageSize: response.initialQueryParameters.pageSize },
      };
    },
  });
  return {
    isInitialPropsError: isError,
    initialPropsError: error,
    isLoadingInitialProps: isLoading,
    initialProps: data,
  };
}

function isExpectedResponse(res: unknown): res is CourseMappingsPagePropsResponse {
  return Boolean(res && typeof res === 'object');
}
